import { FeatureAccessSettingsClients } from '@ms/yammer-graphql';
import queryFeatureAccessSettings from '@ms/yammer-graphql/dist/operations/queryFeatureAccessSettings.graphql';

import {
  GraphqlApiTreatmentRecord,
  ManagedApiLifecycle,
  getGraphqlApiRequest,
  getTreatmentsExtensionForGraphqlBody,
  managedApiAction,
} from '../../api';
import { normalizeFeatureAccessSettings } from '../../normalization/graphql/normalizeFeatureAccessSettings';
import { Experiment } from '../../state/ecsExperiments/types';
import { reportError } from '../../telemetry/api';
import { ErrorCode } from '../../telemetry/events';
import { PromiseThunkAction } from '../../types';

import {
  FeatureAccessSettingsClientLoadFulfilledAction,
  FeatureAccessSettingsClientLoadPendingAction,
  FeatureAccessSettingsClientLoadRejectedAction,
} from './actions.types';
import { getFeatureAccessSettingsStatus } from './selectors';

interface FeatureAccessSettingsResponse {
  readonly data: FeatureAccessSettingsClients;
}

const loadFeatureAccessSettingsPending = (): FeatureAccessSettingsClientLoadPendingAction => ({
  type: 'FEATURE_ACCESS_SETTINGS.LOAD_PENDING',
});

const loadFeatureAccessSettingsFulfilled = (
  payload: FeatureAccessSettingsClientLoadFulfilledAction['payload']
): FeatureAccessSettingsClientLoadFulfilledAction => ({
  type: 'FEATURE_ACCESS_SETTINGS.LOAD_FULFILLED',
  payload,
});

const loadFeatureAccessSettingsRejected = (error: unknown): FeatureAccessSettingsClientLoadRejectedAction => ({
  type: 'FEATURE_ACCESS_SETTINGS.LOAD_REJECTED',
  payload: {
    error,
  },
  error: true,
});

const loadFeatureAccessSettingsRequest = (treatments: GraphqlApiTreatmentRecord[]) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'FeatureAccessSettingsClients',
      query: queryFeatureAccessSettings,
    },
    treatments,
  });

const loadFeatureAccessSettingsLifecycle: ManagedApiLifecycle<FeatureAccessSettingsResponse> = {
  pending: () => loadFeatureAccessSettingsPending(),
  fulfilled: (rawFeatureAccessSettings) => {
    const payload = normalizeFeatureAccessSettings(rawFeatureAccessSettings);

    return loadFeatureAccessSettingsFulfilled(payload);
  },
  rejected: (error) => loadFeatureAccessSettingsRejected(error),
};

type Load = () => PromiseThunkAction<void>;
export const load: Load = () => async (dispatch, getState) => {
  const state = getState();
  const status = getFeatureAccessSettingsStatus(state);
  const treatments = getTreatmentsExtensionForGraphqlBody({
    state,
    experiments: [
      Experiment.UserStoryline,
      Experiment.Stories,
      Experiment.VivaEngageLC,
      Experiment.StorylineAnnouncement,
    ],
  });

  if (status === 'pending') {
    return;
  }

  const action = managedApiAction({
    request: loadFeatureAccessSettingsRequest(treatments),
    lifecycle: loadFeatureAccessSettingsLifecycle,
  });

  try {
    await dispatch(action);
  } catch (error) {
    reportError({
      error,
      eventProperties: {
        errorCode: ErrorCode.FeatureAccessSettingsFailed,
      },
    });
  }
};
